/**
 * Cloud Save Service
 * Handles synchronization of save data with the backend API
 */

import { apiService } from './api.service';
import type { SaveResponseDTO } from '@shared/index';

export interface CloudSave {
  id: number;
  romHash: string;
  romName: string | null;
  saveSlot: number;
  fileSize: number;
  createdAt: string;
  updatedAt: string;
}

export class CloudSaveService {
  /**
   * Upload save data to cloud
   */
  async uploadSave(
    romHash: string,
    saveData: Uint8Array,
    romName?: string,
    saveSlot: number = 0
  ): Promise<SaveResponseDTO> {
    return apiService.uploadSave(romHash, saveData, romName, saveSlot);
  }

  /**
   * Download save data from cloud
   */
  async downloadSave(saveId: number): Promise<Uint8Array> {
    const arrayBuffer = await apiService.downloadSave(saveId);
    return new Uint8Array(arrayBuffer);
  }

  /**
   * Get save info by ROM hash
   */
  async getSaveByRomHash(romHash: string, saveSlot: number = 0): Promise<SaveResponseDTO | null> {
    try {
      return await apiService.getSaveByRomHash(romHash, saveSlot);
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get all saves for current user
   */
  async getAllSaves(): Promise<CloudSave[]> {
    const response = await apiService.getSaves();
    return response.saves.map(this.mapToCloudSave);
  }

  /**
   * Delete a save from cloud
   */
  async deleteSave(saveId: number): Promise<void> {
    await apiService.deleteSave(saveId);
  }

  /**
   * Check if cloud save exists and is newer than local
   */
  async compareWithLocal(
    romHash: string,
    localTimestamp: number,
    saveSlot: number = 0
  ): Promise<{
    exists: boolean;
    isNewer: boolean;
    cloudSave: SaveResponseDTO | null;
  }> {
    const cloudSave = await this.getSaveByRomHash(romHash, saveSlot);

    if (!cloudSave) {
      return {
        exists: false,
        isNewer: false,
        cloudSave: null,
      };
    }

    const cloudTimestamp = new Date(cloudSave.updatedAt).getTime();
    const isNewer = cloudTimestamp > localTimestamp;

    return {
      exists: true,
      isNewer,
      cloudSave,
    };
  }

  /**
   * Sync save: upload if local is newer, download if cloud is newer
   */
  async syncSave(
    romHash: string,
    localSaveData: Uint8Array | null,
    localTimestamp: number,
    romName?: string,
    saveSlot: number = 0
  ): Promise<{
    action: 'uploaded' | 'downloaded' | 'none' | 'conflict';
    saveData: Uint8Array | null;
    cloudSave: SaveResponseDTO | null;
  }> {
    const comparison = await this.compareWithLocal(romHash, localTimestamp, saveSlot);

    // No cloud save exists - upload local if available
    if (!comparison.exists) {
      if (localSaveData) {
        const cloudSave = await this.uploadSave(romHash, localSaveData, romName, saveSlot);
        return {
          action: 'uploaded',
          saveData: localSaveData,
          cloudSave,
        };
      }
      return {
        action: 'none',
        saveData: null,
        cloudSave: null,
      };
    }

    // Cloud save is newer - download
    if (comparison.isNewer) {
      const saveData = await this.downloadSave(comparison.cloudSave!.id);
      return {
        action: 'downloaded',
        saveData,
        cloudSave: comparison.cloudSave,
      };
    }

    // Local is newer - upload
    if (localSaveData) {
      const cloudSave = await this.uploadSave(romHash, localSaveData, romName, saveSlot);
      return {
        action: 'uploaded',
        saveData: localSaveData,
        cloudSave,
      };
    }

    return {
      action: 'none',
      saveData: null,
      cloudSave: comparison.cloudSave,
    };
  }

  /**
   * Map DTO to CloudSave
   */
  private mapToCloudSave(dto: SaveResponseDTO): CloudSave {
    return {
      id: dto.id,
      romHash: dto.romHash,
      romName: dto.romName,
      saveSlot: dto.saveSlot,
      fileSize: dto.fileSize,
      createdAt: dto.createdAt,
      updatedAt: dto.updatedAt,
    };
  }
}

// Singleton instance
export const cloudSaveService = new CloudSaveService();
